// pages/Idiomas.js - Versión simplificada sin componentes problemáticos
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Divider,
  Card,
  InputNumber
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  ExportOutlined,
  GlobalOutlined,
  FlagOutlined,
  UpOutlined,
  DownOutlined,
  SwapOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;

const Idiomas = () => {
  // Estados
  const [idiomas, setIdiomas] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [globalSearchText, setGlobalSearchText] = useState('');
  const [paises, setPaises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentIdioma, setCurrentIdioma] = useState(null);
  const [form] = Form.useForm();
  const [tableLoading, setTableLoading] = useState(false);
  const [reordenarModalVisible, setReordenarModalVisible] = useState(false);
  const [idiomasReordenar, setIdiomasReordenar] = useState([]);
  
  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Cargar datos iniciales
  useEffect(() => {
    fetchIdiomas();
    fetchPaises();
  }, []);

  const fetchIdiomas = async () => {
    setTableLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/idiomas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIdiomas(response.data.data);
      setFilteredData(response.data.data);
      
      console.log('✅ Idiomas cargados:', response.data.data);
    } catch (error) {
      message.error('Error al cargar los idiomas');
      console.error('Error fetchIdiomas:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchPaises = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/idiomas/paises/listado`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaises(response.data.data);
      console.log('✅ Países cargados:', response.data.data);
    } catch (error) {
      console.error('Error cargando países:', error);
    }
  };

  // Función para búsqueda global
  const handleGlobalSearch = (searchText) => {
    setGlobalSearchText(searchText);
    
    if (!searchText.trim()) {
      setFilteredData(idiomas);
      return;
    }
    
    const lowerSearchText = searchText.toLowerCase().trim();
    
    const filtered = idiomas.filter(record => {
      return Object.keys(record).some(key => {
        const value = record[key];
        
        if (value === null || value === undefined) return false;
        
        const stringValue = String(value).toLowerCase();
        return stringValue.includes(lowerSearchText);
      });
    });
    
    setFilteredData(filtered);
  };

  // Configuración de columnas
  const columns = [
    {
      title: 'Orden',
      dataIndex: 'i',
      key: 'i',
      width: 80,
      sorter: (a, b) => a.i - b.i,
      sortOrder: sortedInfo.columnKey === 'i' && sortedInfo.order,
      render: (orden) => (
        <Badge 
          count={orden} 
          style={{ backgroundColor: '#1890ff' }}
        />
      ),
    },
    {
      title: 'Código',
      dataIndex: 'Codigo',
      key: 'Codigo',
      width: 100,
      sorter: (a, b) => a.Codigo.localeCompare(b.Codigo),
      sortOrder: sortedInfo.columnKey === 'Codigo' && sortedInfo.order,
      render: (codigo) => (
        <Tag color="blue" style={{ fontSize: '12px', fontWeight: 'bold' }}>
          {codigo}
        </Tag>
      ),
    },
    {
      title: 'Descripción',
      dataIndex: 'Descripcion',
      key: 'Descripcion',
      width: 200,
      sorter: (a, b) => a.Descripcion.localeCompare(b.Descripcion),
      sortOrder: sortedInfo.columnKey === 'Descripcion' && sortedInfo.order,
      ellipsis: true,
    },
    {
      title: 'País',
      dataIndex: 'Pais',
      key: 'Pais',
      width: 120,
      sorter: (a, b) => (a.Pais || '').localeCompare(b.Pais || ''),
      sortOrder: sortedInfo.columnKey === 'Pais' && sortedInfo.order,
      render: (pais) => (
        <Space>
          {pais && <FlagOutlined style={{ color: '#1890ff' }} />}
          <Text strong={!!pais}>{pais || 'Sin país'}</Text>
        </Space>
      ),
      filters: paises.map(pais => ({
        text: `${pais.nombre} (${pais.codigo})`,
        value: pais.codigo
      })),
      filteredValue: filteredInfo.Pais || null,
      onFilter: (value, record) => record.Pais === value,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 220,
      fixed: 'right',
      render: (_, record, index) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Tooltip title="Subir posición">
            <Button
              type="default"
              icon={<UpOutlined />}
              size="small"
              disabled={index === 0}
              onClick={() => moverArriba(record.Codigo)}
            />
          </Tooltip>
          
          <Tooltip title="Bajar posición">
            <Button
              type="default"
              icon={<DownOutlined />}
              size="small"
              disabled={index === filteredData.length - 1}
              onClick={() => moverAbajo(record.Codigo)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar idioma?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDelete(record.Codigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
          
          <Tooltip title="Ver detalle">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Handlers para las acciones
  const handleCreate = () => {
    setModalType('create');
    setCurrentIdioma(null);
    form.resetFields();
    form.setFieldsValue({
      i: idiomas.length > 0 ? Math.max(...idiomas.map(i => i.i)) + 1 : 1
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentIdioma(record);
    
    form.setFieldsValue({
      ...record,
      Codigo: record.Codigo,
      Descripcion: record.Descripcion || '',
      Pais: record.Pais || '',
      i: record.i || 1
    });
    setModalVisible(true);
  };

  const handleView = (record) => {
    Modal.info({
      title: `Detalle de Idioma: ${record.Descripcion}`,
      width: 600,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <strong>Código:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="blue" style={{ fontSize: '14px' }}>
                  {record.Codigo}
                </Tag>
              </div>
            </Col>
            <Col span={8}>
              <strong>Orden:</strong>
              <div style={{ marginTop: 4 }}>
                <Badge count={record.i} style={{ backgroundColor: '#1890ff', fontSize: '14px' }} />
              </div>
            </Col>
            <Col span={8}>
              <strong>País:</strong>
              <div style={{ marginTop: 4 }}>
                {record.Pais ? (
                  <Space>
                    <FlagOutlined />
                    <Text strong>{record.Pais}</Text>
                  </Space>
                ) : (
                  <Text type="secondary">Sin asignar</Text>
                )}
              </div>
            </Col>
            <Col span={24}>
              <Divider />
            </Col>
            <Col span={24}>
              <strong>Descripción:</strong>
              <div style={{ marginTop: 8, padding: 12, backgroundColor: '#f6f6f6', borderRadius: 6 }}>
                <Text style={{ fontSize: '16px' }}>{record.Descripcion}</Text>
              </div>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleDelete = async (codigo) => {
    try {
      setTableLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${BASE_URL}/api/idiomas/${codigo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const nuevosIdiomas = idiomas.filter(i => i.Codigo !== codigo);
      setIdiomas(nuevosIdiomas);
      
      const nuevoFilteredData = filteredData.filter(i => i.Codigo !== codigo);
      setFilteredData(nuevoFilteredData);
      
      message.success('Idioma eliminado correctamente');
      
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar el idioma');
      console.error('Error handleDelete:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      
      if (modalType === 'create') {
        const response = await axios.post(
          `${BASE_URL}/api/idiomas`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const nuevoIdioma = response.data.data;
        
        setIdiomas([...idiomas, nuevoIdioma]);
        
        if (globalSearchText) {
          const lowerSearchText = globalSearchText.toLowerCase();
          const shouldShowInSearch = Object.keys(nuevoIdioma).some(key => {
            const value = nuevoIdioma[key];
            if (value === null || value === undefined) return false;
            return String(value).toLowerCase().includes(lowerSearchText);
          });
          
          if (shouldShowInSearch) {
            setFilteredData([...filteredData, nuevoIdioma]);
          }
        } else {
          setFilteredData([...filteredData, nuevoIdioma]);
        }
        
        message.success('Idioma creado correctamente');
        
      } else {
        const response = await axios.put(
          `${BASE_URL}/api/idiomas/${currentIdioma.Codigo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const idiomaActualizado = response.data.data;
        
        const nuevosIdiomas = idiomas.map(i => 
          i.Codigo === currentIdioma.Codigo ? idiomaActualizado : i
        );
        
        setIdiomas(nuevosIdiomas);
        
        const nuevoFilteredData = filteredData.map(i => 
          i.Codigo === currentIdioma.Codigo ? idiomaActualizado : i
        );
        
        setFilteredData(nuevoFilteredData);
        
        message.success('Idioma actualizado correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      
    } catch (error) {
      console.error('❌ Error en handleSave:', error);
      
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('Error al guardar los datos');
      }
    } finally {
      setLoading(false);
    }
  };

  // Funciones para mover idiomas arriba/abajo
  const moverArriba = async (codigo) => {
    const idiomaIndex = idiomas.findIndex(i => i.Codigo === codigo);
    if (idiomaIndex <= 0) return;
    
    await intercambiarPosiciones(idiomaIndex, idiomaIndex - 1);
  };

  const moverAbajo = async (codigo) => {
    const idiomaIndex = idiomas.findIndex(i => i.Codigo === codigo);
    if (idiomaIndex >= idiomas.length - 1) return;
    
    await intercambiarPosiciones(idiomaIndex, idiomaIndex + 1);
  };

  const intercambiarPosiciones = async (index1, index2) => {
    try {
      setTableLoading(true);
      const token = localStorage.getItem('token');
      
      // Crear nuevo array con posiciones intercambiadas
      const nuevosIdiomas = [...idiomas];
      const temp = nuevosIdiomas[index1];
      nuevosIdiomas[index1] = nuevosIdiomas[index2];
      nuevosIdiomas[index2] = temp;
      
      // Actualizar valores de i
      const idiomasParaActualizar = nuevosIdiomas.map((idioma, index) => ({
        Codigo: idioma.Codigo,
        i: index + 1
      }));
      
      // Enviar al backend
      const response = await axios.post(
        `${BASE_URL}/api/idiomas/reordenar`,
        { orden: idiomasParaActualizar },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Actualizar estado local
      setIdiomas(response.data.data);
      setFilteredData(response.data.data);
      
      message.success('Posición actualizada correctamente');
      
    } catch (error) {
      console.error('❌ Error intercambiando posiciones:', error);
      message.error('Error al actualizar la posición');
    } finally {
      setTableLoading(false);
    }
  };

  // Función para reordenar en modal
  const abrirModalReordenar = () => {
    setIdiomasReordenar([...idiomas]);
    setReordenarModalVisible(true);
  };

  const moverArribaReordenar = (index) => {
    if (index <= 0) return;
    
    const nuevosIdiomas = [...idiomasReordenar];
    const temp = nuevosIdiomas[index];
    nuevosIdiomas[index] = nuevosIdiomas[index - 1];
    nuevosIdiomas[index - 1] = temp;
    
    // Actualizar valores de i
    const idiomasActualizados = nuevosIdiomas.map((idioma, idx) => ({
      ...idioma,
      i: idx + 1
    }));
    
    setIdiomasReordenar(idiomasActualizados);
  };

  const moverAbajoReordenar = (index) => {
    if (index >= idiomasReordenar.length - 1) return;
    
    const nuevosIdiomas = [...idiomasReordenar];
    const temp = nuevosIdiomas[index];
    nuevosIdiomas[index] = nuevosIdiomas[index + 1];
    nuevosIdiomas[index + 1] = temp;
    
    // Actualizar valores de i
    const idiomasActualizados = nuevosIdiomas.map((idioma, idx) => ({
      ...idioma,
      i: idx + 1
    }));
    
    setIdiomasReordenar(idiomasActualizados);
  };

  const guardarReordenamiento = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const ordenParaGuardar = idiomasReordenar.map((idioma, index) => ({
        Codigo: idioma.Codigo,
        i: index + 1
      }));
      
      const response = await axios.post(
        `${BASE_URL}/api/idiomas/reordenar`,
        { orden: ordenParaGuardar },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setIdiomas(response.data.data);
      setFilteredData(response.data.data);
      setReordenarModalVisible(false);
      
      message.success('Orden guardado correctamente');
      
    } catch (error) {
      console.error('❌ Error guardando orden:', error);
      message.error('Error al guardar el orden');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    
    let result = [...idiomas];
    
    Object.keys(filters).forEach(key => {
      const filterValues = filters[key];
      if (filterValues && filterValues.length > 0) {
        result = result.filter(item => {
          const itemValue = item[key];
          
          if (key === 'Pais') {
            return filterValues.includes(itemValue);
          }
          
          return true;
        });
      }
    });
    
    if (sorter.field && sorter.order) {
      result.sort((a, b) => {
        const aVal = a[sorter.field] || '';
        const bVal = b[sorter.field] || '';
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          if (sorter.order === 'ascend') {
            return aVal.localeCompare(bVal);
          } else {
            return bVal.localeCompare(aVal);
          }
        } else {
          if (sorter.order === 'ascend') {
            return (aVal || 0) - (bVal || 0);
          } else {
            return (bVal || 0) - (aVal || 0);
          }
        }
      });
    }
    
    if (globalSearchText) {
      const lowerSearchText = globalSearchText.toLowerCase().trim();
      result = result.filter(record => {
        return Object.keys(record).some(key => {
          const value = record[key];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(lowerSearchText);
        });
      });
    }
    
    setFilteredData(result);
  };

  const clearFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setGlobalSearchText('');
    setFilteredData(idiomas);
    message.success('Filtros y búsqueda limpiados');
  };

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Idiomas" 
          value={idiomas.length}
          prefix={<GlobalOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Con País" 
          value={idiomas.filter(i => i.Pais).length}
          prefix={<FlagOutlined />}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Europeos" 
          value={idiomas.filter(i => ['ESP', 'GB', 'FRA', 'GER', 'ITA', 'POR'].includes(i.Pais)).length}
          valueStyle={{ color: '#faad14' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Latinoamericanos" 
          value={idiomas.filter(i => ['ARG', 'MEX', 'COL', 'BRA'].includes(i.Pais)).length}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
    </Row>
  );

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input.Search
          placeholder="Buscar por código, descripción, país..."
          value={globalSearchText}
          style={{ width: 400 }}
          allowClear
          enterButton
          onChange={(e) => {
            setGlobalSearchText(e.target.value);
            if (e.target.value === '') {
              handleGlobalSearch('');
            }
          }}
          onSearch={handleGlobalSearch}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={clearFilters}
          size="small"
        >
          Limpiar
        </Button>
        <Button
          icon={<SwapOutlined />}
          onClick={abrirModalReordenar}
          size="small"
          type="primary"
        >
          Reordenar
        </Button>
        <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
          <strong>{filteredData.length}</strong> idiomas
        </Tag>
      </Space>
    </div>
  );

  return (
    <PageContainer
      title="Gestión de Idiomas"
      subtitle={`${idiomas.length} idiomas registrados`}
      icon={<GlobalOutlined />}
      loading={tableLoading}
      onRefresh={fetchIdiomas}
      onCreate={handleCreate}
      badgeCount={idiomas.length}
      badgeStatus="processing"
    >
      {/* Estadísticas */}
      {estadisticas}

      {searchBar}
      
      {/* Tabla */}
      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="Codigo"
        loading={tableLoading}
        onChange={handleChange}
        scroll={{ x: 1000 }}
        style={{
          borderRadius: '8px',
          overflow: 'hidden'
        }}
      />

      {/* Modal para crear/editar */}
      <Modal
        title={modalType === 'create' ? 'Nuevo Idioma' : 'Editar Idioma'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="Codigo"
                label="Código"
                rules={[
                  { required: true, message: 'Por favor ingrese el código' },
                  { max: 10, message: 'El código debe tener máximo 10 caracteres' }
                ]}
              >
                <Input 
                  placeholder="Ej: ESP" 
                  disabled={modalType === 'edit'}
                  maxLength={10}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="i"
                label="Orden"
                rules={[
                  { required: true, message: 'Por favor ingrese el orden' }
                ]}
              >
                <InputNumber 
                  placeholder="1" 
                  style={{ width: '100%' }}
                  min={1}
                  max={999}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="Pais"
                label="País (Código)"
              >
                <Select
                  showSearch
                  placeholder="Seleccione país"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                >
                  {paises.map(pais => (
                    <Option key={pais.codigo} value={pais.codigo}>
                      {pais.nombre} ({pais.codigo})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="Descripcion"
                label="Descripción"
                rules={[
                  { required: true, message: 'Por favor ingrese la descripción' },
                  { max: 100, message: 'La descripción debe tener máximo 100 caracteres' }
                ]}
              >
                <Input 
                  placeholder="Nombre completo del idioma" 
                  maxLength={100}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Modal para reordenar */}
      <Modal
        title="Reordenar Idiomas"
        open={reordenarModalVisible}
        onCancel={() => setReordenarModalVisible(false)}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => setReordenarModalVisible(false)}>
            Cancelar
          </Button>,
          <Button key="save" type="primary" onClick={guardarReordenamiento} loading={loading}>
            Guardar Orden
          </Button>
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Usa los botones de flecha para cambiar el orden de los idiomas
          </Text>
        </div>

        <div style={{ maxHeight: '400px', overflow: 'auto' }}>
          {idiomasReordenar.map((idioma, index) => (
            <Card
              key={idioma.Codigo}
              size="small"
              style={{
                marginBottom: 8,
                borderLeft: '4px solid #1890ff'
              }}
            >
              <Row align="middle" gutter={16}>
                <Col span={2}>
                  <Badge count={idioma.i} style={{ backgroundColor: '#1890ff' }} />
                </Col>
                <Col span={4}>
                  <Tag color="blue">{idioma.Codigo}</Tag>
                </Col>
                <Col span={10}>
                  <Text strong>{idioma.Descripcion}</Text>
                </Col>
                <Col span={4}>
                  {idioma.Pais ? (
                    <Space>
                      <FlagOutlined />
                      <Text>{idioma.Pais}</Text>
                    </Space>
                  ) : (
                    <Text type="secondary">Sin país</Text>
                  )}
                </Col>
                <Col span={4}>
                  <Space>
                    <Button
                      type="text"
                      icon={<UpOutlined />}
                      size="small"
                      disabled={index === 0}
                      onClick={() => moverArribaReordenar(index)}
                    />
                    <Button
                      type="text"
                      icon={<DownOutlined />}
                      size="small"
                      disabled={index === idiomasReordenar.length - 1}
                      onClick={() => moverAbajoReordenar(index)}
                    />
                  </Space>
                </Col>
              </Row>
            </Card>
          ))}
        </div>
      </Modal>
    </PageContainer>
  );
};

export default Idiomas;